import React, { useContext, useMemo } from 'react';
import { css } from '@compiled/react';
import { media } from '@atlaskit/primitives/responsive';
import { token } from '@atlaskit/tokens';
import { useLoaderData } from 'react-router-dom';
import { useDeviceSize } from '@atlassian/learning-components';

import { HelmetContext } from 'src/components/Helmet';
import { useScreenEvent } from 'src/hooks/useScreenEvent';
import ContentSection, { SupportedContentSectionTypes } from 'src/pages/hubs/shared/children/ContentSection';
import PageHeading from 'src/pages/hubs/shared/children/PageHeading';

import { DynamicHubLoaderProps } from './loader';

export const DynamicHubPage: React.FC = () => {
  const helmet = useContext(HelmetContext);
  const deviceWidth = useDeviceSize();
  const isMobile = deviceWidth < 768;

  const { dynamicHubData } = useLoaderData() as DynamicHubLoaderProps;

  helmet.setHelmet({
    title: `${dynamicHubData.titleExternal} | Learning - Atlassian Community`,
    seoTags: dynamicHubData.seo,
  });

  // Send screen event for analytics
  useScreenEvent({ eventName: 'dynamicHubScreen', additionalData: { appName: dynamicHubData.titleExternal } });

  const contentSections = useMemo(
    () =>
      dynamicHubData.contentContainerCollection.items.map((contentBlock) => (
        <ContentSection
          key={(contentBlock as SupportedContentSectionTypes).sys.id}
          contentBlock={contentBlock}
          optionalProps={{ containerMaxWidth: 1070 }}
        />
      )),
    [dynamicHubData.contentContainerCollection.items],
  );

  // Extract app name from titleExternal for dynamic content
  const appName = dynamicHubData.titleExternal;
  const appSlug = appName.toLowerCase().replace(/\s+/g, '-');

  return (
    <main data-testid="dynamic-hub" css={pageContainerStyles}>
      <PageHeading
        title={dynamicHubData.hero.titleExternal}
        richTitle={dynamicHubData.hero.richTitle}
        subtitle={dynamicHubData.hero.subtitle}
        containerStyles={
          !isMobile
            ? {
              paddingTop: `calc(${token('space.1000')} + 15px)`,
              paddingBottom: `${token('space.600')}`,
            }
            : undefined
        }
        keyword={appName}
        logoSrc={dynamicHubData.hero.asset?.image.url || `/images/logos/${appSlug}.svg`}
        logoAlt={`${appName} logo`}
        bannerDesktopSrc={dynamicHubData.hero.backgroundImageDesktop?.image.url}
        bannerMobileSrc={dynamicHubData.hero.backgroundImageMobile?.image.url}
        bannerMobileAlt={appName}
      />

      {/* content */}
      <div data-testid="dynamic-hub-content-section" css={outerContentContainerStyles}>
        <div css={innerContentContainerStyles}>{contentSections}</div>
      </div>
    </main>
  );
};

const pageContainerStyles = css({
  display: 'flex',
  justifyContent: 'center',
  flexDirection: 'column',
  alignItems: 'center',
  marginTop: '136px',
  gap: token('space.1000'),

  '@media (max-width: 1279px)': {
    marginTop: '0',
  },

  '& [data-testid="hero"]': {
    margin: '0 auto',
  },

  [media.below.md]: {
    gap: token('space.600'),
  },
});

const outerContentContainerStyles = css({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',

  [media.below.md]: {
    paddingTop: 0,
  },
});

const innerContentContainerStyles = css({
  maxWidth: '1320px',
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  rowGap: `calc(${token('space.1000')} * 2)`,
  alignSelf: 'center',

  [media.below.md]: {
    rowGap: `calc(${token('space.600')} * 2)`,

    '& > div': {
      paddingLeft: token('space.200'),
      paddingRight: token('space.200'),
    },
  },
});
